// IAM / staff-provisioning contracts — the admin console's view of the source-verified
// /v1/admin/staff API (apps/api/src/modules/iam). Types ONLY; the API is the boundary
// and re-enforces every grant. Grounded 1:1 on the controller + StaffProvisioningService
// + createStaffSchema (@csa/validation, packages/validation/src/iam-staff.ts):
//   - POST /v1/admin/staff → StaffCreatedSummary (created staff + ONE-TIME temp password)
//     RBAC: staff:write@all gates create; ELEVATED (ADMIN/SUPER_ADMIN) creation and
//     cross-org provisioning additionally require staff.admin:write (SUPER_ADMIN only).
//   - GET  /v1/admin/staff       → Page<StaffSummary> (list; staff:read@all)
//   - PATCH /v1/admin/staff/:id   → StaffManagedSummary (rename / role change)
//   - POST /v1/admin/staff/:id/status → StaffManagedSummary (enable/disable)
//   - POST /v1/admin/staff/:id/reset-password → { temporaryPassword } (one-time)

/** The REAL assignable staff roles — kept in lockstep with @csa/validation's
 *  ASSIGNABLE_ROLE_CODES (the boundary allowlist) and packages/db seed BASELINE_ROLES.
 *  Duplicated (not imported) to keep the admin app free of a zod/validation dependency,
 *  matching the "types only" convention of the other *-contracts modules. */
export const ASSIGNABLE_ROLE_CODES = [
  "SUPER_ADMIN",
  "ADMIN",
  "COMPLIANCE",
  "OPS",
  "MANAGER",
  "ADVISOR",
  "CLAIMS_COORDINATOR",
  "FINANCE",
  "CONFIG_ADMIN",
  "ANALYTICS",
] as const;
export type AssignableRoleCode = (typeof ASSIGNABLE_ROLE_CODES)[number];

/** Elevated roles whose creation (and any cross-org provision) is SUPER_ADMIN-only —
 *  the caller needs `staff.admin:write`, not a plain ADMIN's `staff:write`. The service
 *  enforces this regardless; the surface only hides the options as UX. */
export const ELEVATED_ROLE_CODES = ["ADMIN", "SUPER_ADMIN"] as const;
export type ElevatedRoleCode = (typeof ELEVATED_ROLE_CODES)[number];

export const isElevatedRole = (code: string): code is ElevatedRoleCode =>
  (ELEVATED_ROLE_CODES as readonly string[]).includes(code);

/** Create-staff request. `organizationId` is omitted for the common case (defaults to the
 *  caller's org); a differing value is a SUPER_ADMIN cross-org provision, re-checked
 *  server-side. */
export interface CreateStaffInput {
  displayName: string;
  email: string;
  roleCode: AssignableRoleCode;
  organizationId?: string;
}

/** The created staff summary + the ONE-TIME temporary password (returned once, never
 *  stored or re-shown; the surface reveals it and never caches it). */
export interface StaffCreatedSummary {
  id: string;
  displayName: string;
  email: string;
  roleCode: string;
  organizationId: string;
  status: string;
  temporaryPassword: string;
}

/** Staff LIST projection — grounded 1:1 on iam.repository's StaffSummary
 *  (GET /v1/admin/staff, Page envelope). PII-safe (no auth identifiers). `email`
 *  and `roleCode` may be null (unassigned); `enabled` is derived from
 *  status === "ACTIVE" server-side. staff:read@all. */
export interface StaffSummary {
  id: string;
  displayName: string;
  email: string | null;
  roleCode: string | null;
  status: string;
  enabled: boolean;
  teamId: string | null;
  createdAt: string;
}

/** The edit-side summary returned by PATCH /v1/admin/staff/:id and
 *  POST /v1/admin/staff/:id/status (StaffManagementService.StaffManagedSummary). */
export interface StaffManagedSummary {
  id: string;
  displayName: string;
  email: string | null;
  roleCode: string | null;
  status: string;
  enabled: boolean;
}

/** PATCH /v1/admin/staff/:id — at least one of displayName / roleCode (the boundary
 *  schema rejects an empty patch). Changing to/from an ELEVATED role additionally
 *  requires staff.admin:write; the service re-enforces regardless. */
export interface UpdateStaffInput {
  displayName?: string;
  roleCode?: AssignableRoleCode;
}

/** POST /v1/admin/staff/:id/reset-password → a ONE-TIME temporary password (returned
 *  once, never stored; revealed then discarded — never cached). */
export interface ResetStaffPasswordResult {
  temporaryPassword: string;
}
